import React from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import {useNotifications} from "context/NotificationContext";
import {invalidate} from "services/query-client";
import Page from "components/Page";
import {TicketTypeData} from "types/TicketType";
import TicketTypeForm from "pages/TicketType/Form";
import {FormikHelpers} from "formik";
import {useOperator} from "../../../context/OperatorContext";

function MutateTicketType() {
    const {termFor} = useOperator();
    const {id} = useParams()
    const {data: ticketType, isLoading} = useQuery(
        ['ticket-types', {id}],
        () => ngage.ticketTypes.get(parseInt(id as string)),
        {enabled: !!id}
    )

    const store = useMutation((data: TicketTypeData) => ngage.ticketTypes.store(data))
    const update = useMutation((data: TicketTypeData) => ngage.ticketTypes.update(parseInt(id as string), data))

    const {notify} = useNotifications()
    const navigate = useNavigate()

    // Handle form submission
    async function handleSubmit(values: TicketTypeData, {setSubmitting}: FormikHelpers<TicketTypeData>) {
        id? await handleUpdate(values) : await handleStore(values)
        await invalidate('ticket-types.all')

        setSubmitting(false)
        navigate('/ticket-types')
    }

    // Handle store mutation
    async function handleStore(values: TicketTypeData) {
        try {
            await store.mutateAsync(values)
            notify("success", `${termFor('Ticket Type')} Created`, `${values.name} has been created`)
        } catch (error) {
            notify("error", `${termFor('Ticket Type')} Not Created`, `${values.name} could not be stored`)
            throw error
        }
    }

    // Handle update mutation
    async function handleUpdate(values: TicketTypeData) {
        try {
            await update.mutateAsync(values)
            notify("success", `${termFor('Ticket Type')} Updated`, `${values.name} has been updated`)
            await invalidate(['ticket-types', {id}])
        } catch (error) {
            notify("error", `${termFor('Ticket Type')} Not Updated`, `${values.name} could not be updated`)
            throw error
        }
    }

    // Prepare breadcrumbs
    const newBreadcrumbs = {'Commercial': '/', [termFor('Ticket Types')]: '/ticket-types', [`New ${termFor('Ticket Type')}`]: '/ticket-types/new'}
    const editBreadcrumbs = {'Commercial': '/', [termFor('Ticket Types')]: '/ticket-types', [`Edit ${termFor('Ticket Type')}`]: `/ticket-types/${id}/edit`}

    return (
        <Page title={id ? `Edit ${termFor('Ticket Type')}` : `New ${termFor('Ticket Type')}`}
              breadcrumbs={id ? editBreadcrumbs : newBreadcrumbs}
              isLoading={isLoading}>
            <TicketTypeForm ticketType={ticketType} onSubmit={handleSubmit}/>
        </Page>
    );
}

export default MutateTicketType;